import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { slugify } from '@/lib/slugify';
import type { Employee } from '@/types';

export type EditEmployeeModalProps = {
  open: boolean;
  employee: Employee | null;
  onClose: () => void;
  onSave: (oldTabName: string, newTabName: string, newDisplayName: string) => Promise<void>;
  existing: readonly Employee[];
};

export function EditEmployeeModal({
  open,
  employee,
  onClose,
  onSave,
  existing,
}: EditEmployeeModalProps): JSX.Element | null {
  const [displayName, setDisplayName] = useState('');
  const [tabName, setTabName] = useState('');
  const [tabNameManuallyEdited, setTabNameManuallyEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const displayRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && employee) {
      setDisplayName(employee.displayName);
      setTabName(employee.tabName);
      setTabNameManuallyEdited(false);
      setError(null);
      setSubmitting(false);
      setTimeout(() => displayRef.current?.focus(), 50);
    }
  }, [open, employee]);

  if (!open || !employee) return null;

  const suggestedTab = slugify(displayName);
  const effectiveTab = tabNameManuallyEdited ? tabName : suggestedTab || tabName;

  const otherExisting = existing.filter((e) => e.tabName !== employee.tabName);
  const collision = effectiveTab !== '' && otherExisting.some((e) => e.tabName === effectiveTab);
  const tabChanged = effectiveTab !== employee.tabName;
  const displayChanged = displayName.trim() !== employee.displayName;

  const canSubmit = !submitting && effectiveTab.length > 0 && !collision && (tabChanged || displayChanged);

  const handleDisplayChange = (val: string) => {
    setDisplayName(val);
    if (!tabNameManuallyEdited) {
      setTabName(slugify(val));
    }
  };

  const handleTabChange = (val: string) => {
    setTabName(val);
    setTabNameManuallyEdited(true);
  };

  const applySuggested = () => {
    setTabName(suggestedTab);
    setTabNameManuallyEdited(true);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSave(employee.tabName, effectiveTab, displayName.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-employee-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-surface shadow-lg p-6 space-y-4">
        <header className="flex items-start justify-between">
          <div>
            <h2 id="edit-employee-title" className="text-lg font-semibold">
              Edit Employee
            </h2>
            <p className="text-sm text-muted">
              Update display name and/or the Google Sheet tab identifier.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded hover:bg-surface-2"
          >
            <X className="w-4 h-4" aria-hidden />
          </button>
        </header>

        <div className="space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="edit-display-name">
              Display name
            </label>
            <input
              ref={displayRef}
              id="edit-display-name"
              type="text"
              value={displayName}
              onChange={(e) => handleDisplayChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleSubmit();
                } else if (e.key === 'Escape') {
                  onClose();
                }
              }}
              placeholder="e.g. Jocelyn Vander Tuin"
              className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-sm"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium" htmlFor="edit-tab-name">
                Sheet tab name (slug)
              </label>
              <button
                type="button"
                onClick={applySuggested}
                className="text-xs text-primary hover:underline"
              >
                Use suggested
              </button>
            </div>
            <input
              id="edit-tab-name"
              type="text"
              value={tabName}
              onChange={(e) => handleTabChange(e.target.value)}
              className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 font-mono text-sm"
            />
            <p className="text-xs text-muted">
              Must be unique, lowercase letters, numbers, and hyphens. Changing this renames the tab in the Google Sheet.
            </p>
            {suggestedTab && suggestedTab !== tabName && !tabNameManuallyEdited && (
              <p className="text-xs text-muted">
                Suggested from display: <code className="font-mono">{suggestedTab}</code>
              </p>
            )}
            {collision ? (
              <p className="text-xs text-danger">
                Another employee already uses this tab name.
              </p>
            ) : null}
          </div>
        </div>

        {error ? (
          <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm">
            <p className="font-medium text-danger">Save failed</p>
            <p className="mt-1 text-fg/80 break-words">{error}</p>
          </div>
        ) : null}

        <footer className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm hover:bg-border"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="rounded-md bg-primary text-primary-fg px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving…' : 'Save Changes'}
          </button>
        </footer>
      </div>
    </div>
  );
}
